import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { format, isValid, parse } from 'date-fns';
import { DatepickerProvider } from '../../Datepicker/context/context';
import { Datepicker } from '../../Datepicker/Datepicker';
import { DebouncedTextInput } from '../DebouncedTextInput';
import { DashboardFilter, FilterOptionsResult } from '../types';
import { useMultiSelectComponents, singleSelectComponents } from '../multiSelectComponents';
import { resolveDateRangeDefault } from '../relativeDatetime';
import { useDashboard } from '../contexts/DashboardContext';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { ampli } from '../../../ampli';
import styles from './FilterBar.module.scss';
import { is } from 'date-fns/locale';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const isIsoDate = (v: unknown): v is string => typeof v === 'string' && ISO_DATE_PATTERN.test(v);

interface FilterBarProps {
  filters: DashboardFilter[];
  filterOptions: Record<string, FilterOptionsResult>;
  filterValues: Record<string, any>;
  onFilterChange: (parameterName: string, value: any) => void;
  onClearAll: () => void;
}

type SelectOption = { value: any; label: string; stringValue: string };

/** Multi-select filter that opens the dropdown when all options are removed, falling back to a default on close. */
const MultiSelectFilter: React.FC<{
  filter: DashboardFilter;
  selectOptions: SelectOption[];
  currentValue: any;
  isLoading: boolean;
  onChange: (parameterName: string, value: any) => void;
  components?: any;
}> = ({ filter, selectOptions, currentValue, isLoading, onChange, components }) => {
  const { t } = useTranslation();
  const selectRef = useRef<any>(null);
  const [forceMenuOpen, setForceMenuOpen] = useState(false);

  const allOption = filter.config?.allow_all
    ? { value: '__all__' as any, label: t('All'), stringValue: '__all__' }
    : null;
  const fullOptions = allOption ? [allOption, ...selectOptions] : selectOptions;

  const isAll = currentValue === '__all__';
  const selected = isAll
    ? (allOption ? [allOption] : [])
    : Array.isArray(currentValue)
      ? selectOptions.filter((o) => currentValue.some((v: any) => String(v) === o.stringValue))
      : [];

  const getFallbackValue = useCallback(() => {
    if (filter.defaultValue != null) return filter.defaultValue;
    if (allOption) return '__all__';
    if (selectOptions.length > 0) return [selectOptions[0].value];
    return undefined;
  }, [filter.defaultValue, allOption, selectOptions]);

  const handleChange = useCallback((opts: any, actionMeta?: any) => {
    if (actionMeta?.action === 'clear') {
      onChange(filter.parameterName, getFallbackValue());
      return;
    }
    const selectedOpts = opts as SelectOption[];
    const hasAll = selectedOpts?.some((o) => o.value === '__all__');

    if (hasAll) {
      if (isAll && selectedOpts.length > 1) {
        const values = selectedOpts.filter((o) => o.value !== '__all__').map((o) => o.value);
        onChange(filter.parameterName, values.length > 0 ? values : getFallbackValue());
      } else {
        onChange(filter.parameterName, '__all__');
      }
      return;
    }

    const values = selectedOpts?.map((o) => o.value) ?? [];
    if (values.length === 0) {
      // All options removed → force dropdown open so user can pick
      setForceMenuOpen(true);
      // Focus the select so the menu actually appears
      setTimeout(() => selectRef.current?.focus(), 0);
      return;
    }
    onChange(filter.parameterName, values);
  }, [isAll, filter.parameterName, onChange, getFallbackValue]);

  const handleMenuClose = useCallback(() => {
    if (forceMenuOpen) {
      // User closed the menu after clearing the chips. The select is controlled,
      // so `currentValue` still holds the last committed selection — keep it.
      // Only fall back to the default when nothing at all is selected.
      setForceMenuOpen(false);
      const hasSelection = isAll || (Array.isArray(currentValue) && currentValue.length > 0);
      if (!hasSelection) {
        onChange(filter.parameterName, getFallbackValue());
      }
    }
  }, [forceMenuOpen, filter.parameterName, onChange, getFallbackValue, isAll, currentValue]);

  const defaultOptionValues = useMemo(() => {
    const fallback = getFallbackValue();
    if (fallback == null) return [];
    if (fallback === '__all__') return ['__all__'];
    if (Array.isArray(fallback)) return fallback.map(String);
    return [String(fallback)];
  }, [getFallbackValue]);

  return (
    <Select
      ref={selectRef as any}
      className="wf-select-container"
      classNamePrefix="wf-select"
      options={fullOptions}
      value={selected}
      getOptionValue={(o: SelectOption) => o.stringValue}
      getOptionLabel={(o: SelectOption) => o.label}
      onChange={handleChange}
      onMenuClose={handleMenuClose}
      menuIsOpen={forceMenuOpen || undefined}
      isMulti
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isSearchable
      placeholder={filter.title}
      isLoading={isLoading}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
      components={components}
      {...({ defaultOptionValues } as any)}
    />
  );
};

/** Parse a YYYY-MM-DD string or fall back to `fallback` for anything invalid. */
function safeParseDateBoundary(raw: unknown, fallback: Date): Date {
  if (typeof raw !== 'string' || !raw) return fallback;
  const parsed = parse(raw, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : fallback;
}

/** Wraps the project Datepicker for use with dashboard date_range filters. */
const DateRangeFilter: React.FC<{
  filter: DashboardFilter;
  value: any;
  onChange: (parameterName: string, value: any) => void;
  className?: string;
}> = ({ filter, value, onChange, className }) => {
  const today = new Date();
  const dateValue = value ?? {};
  // Defensive: value may contain stale/raw expressions (e.g. "now-7d") if
  // upstream code forgot to resolve them. Falling back to today keeps the
  // picker from crashing on an invalid `format()` call.
  const startDate = safeParseDateBoundary(dateValue.from, today);
  const endDate = safeParseDateBoundary(dateValue.to, today);

  const handleChange = (start: Date | null, end: Date | null) => {
    if (!start && !end) {
      onChange(filter.parameterName, undefined);
      return;
    }
    const newValue: Record<string, string> = {};
    if (start) newValue.from = format(start, 'yyyy-MM-dd');
    if (end) newValue.to = format(end, 'yyyy-MM-dd');
    onChange(filter.parameterName, Object.keys(newValue).length > 0 ? newValue : undefined);
  };

  return (
    <DatepickerProvider>
      <Datepicker
        onChange={handleChange}
        initStartDate={startDate}
        initEndDate={endDate}
        isRange
        className={className}
      />
    </DatepickerProvider>
  );
};

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  filterOptions,
  filterValues,
  onFilterChange,
  onClearAll,
}) => {
  const { t } = useTranslation();
  const { isEditing, displayedDashboard } = useDashboard();
  const [currentUser] = useCurrentUser();

  const multiSelectComponents = useMultiSelectComponents();

  const dashboardFilters = useMemo(
    () => filters.filter((f) => !f.widgetId && !f.hidden).sort((a, b) => a.order - b.order),
    [filters]
  );

  const handleFilterChangeWithTracking = useCallback((parameterName: string, value: any) => {
    if (!isEditing) {
      const filter = dashboardFilters.find((f) => f.parameterName === parameterName);
      ampli.track({
        event_type: 'Use dashboard filter',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          dashboard_id: displayedDashboard?.id,
          filter_type: filter?.filterType,
          filter_scope: 'global',
          is_super_admin: currentUser?.is_super_admin ?? false,
        },
      });
    }
    onFilterChange(parameterName, value);
  }, [isEditing, dashboardFilters, currentUser, displayedDashboard?.id, onFilterChange]);

  // Auto-populate empty filters once options are loaded
  useEffect(() => {
    dashboardFilters.forEach((filter) => {
      const currentValue = filterValues[filter.parameterName];
      if (currentValue != null) return; // already has a value

      // Date range: use filter's default value or fall back to current year.
      // Relative expressions (e.g. "now-7d") are resolved to concrete dates.
      // Both boundaries must resolve to strict yyyy-MM-dd before applying —
      // otherwise the date picker would receive an invalid date and crash.
      if (filter.filterType === 'date_range') {
        const year = new Date().getFullYear();
        const yearRange = { from: `${year}-01-01`, to: `${year}-12-31` };
        if (filter.defaultValue?.from && filter.defaultValue?.to) {
          const resolved = resolveDateRangeDefault(filter.defaultValue);
          if (isIsoDate(resolved.from) && isIsoDate(resolved.to)) {
            onFilterChange(filter.parameterName, resolved);
          } else {
            onFilterChange(filter.parameterName, yearRange);
          }
        } else {
          onFilterChange(filter.parameterName, yearRange);
        }
        return;
      }

      if (filter.filterType !== 'select' && filter.filterType !== 'multi_select') return;

      const optionsResult = filterOptions[filter.id];
      if (!optionsResult?.options?.length) return; // options not loaded yet

      const opts = optionsResult.options;
      const firstKey = Object.keys(opts[0])[0];
      if (!firstKey) return;

      if (filter.config?.allow_all) {
        onFilterChange(filter.parameterName, '__all__');
      } else {
        // Always use arrays for select/multi_select so queries can use =ANY(...)
        onFilterChange(filter.parameterName, [opts[0][firstKey]]);
      }
    });
  }, [dashboardFilters, filterOptions, filterValues, onFilterChange]);

  if (dashboardFilters.length === 0) return null;

  const allFiltersAtDefault = dashboardFilters.every((filter) => {
    const current = filterValues[filter.parameterName];
    if (current == null) return true;
    if (filter.config?.allow_all && current === '__all__') return true;
    if (filter.defaultValue != null) {
      // date_range defaults may contain relative expressions; compare against resolved form
      const defaultForCompare = filter.filterType === 'date_range'
        ? resolveDateRangeDefault(filter.defaultValue)
        : filter.defaultValue;
      return JSON.stringify(current) === JSON.stringify(defaultForCompare);
    }
    if (filter.filterType === 'date_range') {
      const year = new Date().getFullYear();
      return JSON.stringify(current) === JSON.stringify({ from: `${year}-01-01`, to: `${year}-12-31` });
    }
    if (filter.filterType === 'text') return !current || current === '';
    return false;
  });

  return (
    <div className={styles.filterBar}>
      <div className={styles.filters}>
        {dashboardFilters.map((filter) => {
          const optionsResult = filterOptions[filter.id];
          const options = optionsResult?.options ?? [];

          const selectOptions = options.map((opt) => {
            const keys = Object.keys(opt);
            const rawValue = keys.length > 0 ? opt[keys[0]] : '';
            const rawLabel = keys.length > 1 ? opt[keys[1]] : rawValue;
            return {
              value: rawValue as any,
              label: String(rawLabel ?? ''),
              stringValue: String(rawValue ?? ''),
            };
          });

          const currentValue = filterValues[filter.parameterName];

          if (filter.filterType === 'date_range') {
            return (
              <div key={filter.id} className={styles.filterItem}>
                <label className={styles.filterLabel}>{filter.title}</label>
                <DateRangeFilter
                  filter={filter}
                  value={currentValue}
                  onChange={handleFilterChangeWithTracking}
                  className={styles.datepicker}
                />
              </div>
            );
          }

          if (filter.filterType === 'multi_select') {
            return (
              <div key={filter.id} className={styles.filterItem}>
                <label className={styles.filterLabel}>{filter.title}</label>
                <MultiSelectFilter
                  filter={filter}
                  selectOptions={selectOptions}
                  currentValue={currentValue}
                  isLoading={!optionsResult}
                  onChange={handleFilterChangeWithTracking}
                  components={multiSelectComponents}
                />
              </div>
            );
          }

          if (filter.filterType === 'text') {
            return (
              <div key={filter.id} className={styles.filterItem}>
                <label className={styles.filterLabel}>{filter.title}</label>
                <DebouncedTextInput
                  className={styles.textInput}
                  value={currentValue ?? ''}
                  onChange={(val) => handleFilterChangeWithTracking(filter.parameterName, val && val.trim() ? val : undefined)}
                  placeholder={filter.title}
                />
              </div>
            );
          }

          // Default: 'select' (single select)
          const allOption = filter.config?.allow_all
            ? { value: '__all__' as any, label: t('All'), stringValue: '__all__' }
            : null;
          const fullOptions = allOption ? [allOption, ...selectOptions] : selectOptions;

          // Value is always an array (or __all__), extract first element for single-select display
          const scalarValue = Array.isArray(currentValue) ? currentValue[0] : currentValue;
          const selected = currentValue === '__all__'
            ? allOption
            : scalarValue != null
              ? selectOptions.find((o) => o.stringValue === String(scalarValue)) ?? null
              : null;

          return (
            <div key={filter.id} className={styles.filterItem}>
              <label className={styles.filterLabel}>{filter.title}</label>
              <Select
                className="wf-select-container"
                classNamePrefix="wf-select"
                options={fullOptions}
                value={selected}
                getOptionValue={(o: SelectOption) => o.stringValue}
                getOptionLabel={(o: SelectOption) => o.label}
                onChange={(opt) => {
                  if (opt) {
                    // Always emit arrays for select filters so queries can use =ANY(...)
                    handleFilterChangeWithTracking(filter.parameterName, opt.value === '__all__' ? '__all__' : [opt.value]);
                  } else if (allOption) {
                    handleFilterChangeWithTracking(filter.parameterName, '__all__');
                  } else if (selectOptions.length > 0) {
                    handleFilterChangeWithTracking(filter.parameterName, [selectOptions[0].value]);
                  }
                }}

                isSearchable
                placeholder={filter.title}
                isLoading={!optionsResult}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                components={singleSelectComponents}
              />
            </div>
          );
        })}
      </div>
      <button
        className={styles.clearBtn}
        onClick={onClearAll}
        disabled={allFiltersAtDefault}
      >
        {t('Clear filters')}
      </button>
    </div>
  );
};
